import { useMemo } from "react";
import { useListDataStreams } from "@/api/orval/generated/data-stream";
import { type DashboardSpec, SPEC_VERSION } from "../ChartSpec";
import { DEFAULT_RELATIVE_DURATION } from "../time-window/time-range-slice";
import { buildAllStreamsPanel, toStreamEntry } from "./panel-builders";
import { type ResolvedStream, resolveStreams } from "./resolve-streams";

/** Every stream on one panel. Splitting into per-category panels is a spec transformation, not a generator mode. */
export function getZoneSpec(resolvedStreams: ResolvedStream[]): DashboardSpec {
    const defaultTimeRange = { mode: "relative" as const, duration: DEFAULT_RELATIVE_DURATION };

    if (resolvedStreams.length === 0) {
        return { version: SPEC_VERSION, defaultTimeRange, panels: [] };
    }

    const panel = buildAllStreamsPanel(resolvedStreams.map(toStreamEntry));
    return { version: SPEC_VERSION, defaultTimeRange, panels: panel ? [panel] : [] };
}

/**
 * Think of this as the configuration file for how a zone-based DataViewer should be rendered.
 *
 * @param zoneId - The ID of the zone to build a spec for.
 * @returns {
 *     spec: DashboardSpec;
 *     isLoading: boolean;
 *     isError: boolean;
 *     isEmpty: boolean;
 * }
 */
export function useZoneDataViewerSpec(zoneId: string): {
    spec: DashboardSpec;
    isLoading: boolean;
    isError: boolean;
    isEmpty: boolean;
} {
    const { data, isLoading, isError } = useListDataStreams({ zoneId }, { query: { enabled: !!zoneId } });
    const resolvedStreams: ResolvedStream[] = useMemo(() => (data ? resolveStreams(data) : []), [data]);

    // SpecProvider replaces the document when initialSpec's reference changes — keep it stable.
    const spec = useMemo(() => getZoneSpec(resolvedStreams), [resolvedStreams]);

    return {
        spec,
        isLoading,
        isError,
        isEmpty: !isLoading && !isError && resolvedStreams.length === 0,
    };
}
